import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Trash2,
  Loader2,
  Package,
  User,
  Calendar,
  FileText,
  Calculator,
  MapPin,
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { calculateSalesTotals } from '@/lib/sales-totals';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { CreateCustomerModal } from '@/components/trading/create-customer-modal';
import { CounterpartyPicker } from '@/components/trading/sales/counterparty-picker';
import {
  OrderProductPicker,
  type OrderProduct,
} from '@/components/trading/sales/order-product-picker';

interface OrderItemForm {
  productId?: string;
  productName?: string;
  productCode?: string;
  description?: string; // required for a service line (no productId)
  warehouseId?: string;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  taxRate: number;
  notes?: string;
}

interface SalesOrderForm {
  quoteId?: string;
  customerId?: string;
  supplierId?: string;
  companyName?: string;
  contactName: string;
  email: string;
  phone?: string;
  shippingAddress?: string;
  shippingCity?: string;
  shippingCountry?: string;
  requiredDate?: string;
  currency: string;
  paymentTerms?: string;
  notes?: string;
  items: OrderItemForm[];
}

export function NewSalesOrderPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const [useCustomerAddress, setUseCustomerAddress] = useState(true);

  const { register, handleSubmit, control, watch, setValue, getValues } = useForm<SalesOrderForm>({
    defaultValues: {
      requiredDate: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
      currency: 'EUR',
      items: [],
    },
  });

  const {
    fields: items,
    append: appendItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: 'items',
  });

  // Queries
  const { data: customersData } = trpc.tradingCustomer.list.useQuery({ pageSize: 100 });
  const { data: warehousesData } = trpc.warehouse.list.useQuery({});

  const customers = customersData?.customers ?? [];
  const warehouses = warehousesData ?? [];

  const watchedValues = watch();
  const selectedCustomerId = watchedValues.customerId;

  // Get default warehouse
  const defaultWarehouse = warehouses.find((w) => w.isDefault) || warehouses[0];

  // Imperative price-resolver — used when adding a line, since useQuery is reactive
  // and we need a one-shot lookup at the moment of "add product".
  const utils = trpc.useUtils();

  // Create mutation
  const createMutation = trpc.tradingSalesOrder.create.useMutation({
    onSuccess: (data) => {
      showSuccessToast(t('common.created'));
      navigate(`/trading/sales/orders/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const { subtotal, taxAmount, total } = calculateSalesTotals(watchedValues.items);

  const handleAddProduct = async (product: OrderProduct) => {
    // Check if product already exists in items
    const currentItems = getValues('items');
    const existingIndex = currentItems.findIndex((item) => item.productId === product.id);
    if (existingIndex >= 0) {
      // Increment quantity instead of adding new line
      const currentQty = currentItems[existingIndex]?.quantity || 0;
      setValue(`items.${existingIndex}.quantity`, currentQty + 1, { shouldDirty: true });
      return;
    }

    // Auto-fill price + KDV from the right side of the product based on
    // counterparty kind. Fall back to 0 when the relevant side is unset
    // — the user gets empty inputs and a chance to enter manually.
    const kind: 'customer' | 'vendor' = watchedValues.supplierId ? 'vendor' : 'customer';
    let unitPrice = 0;
    let taxRate = 0;
    try {
      const resolved = await utils.tradingProduct.resolvePrice.fetch({
        productId: product.id,
        kind,
      });
      unitPrice = resolved.unitPrice ?? 0;
      taxRate = resolved.kdvRate ?? 0;
      if (resolved.unitPrice == null) showErrorToast(t('products.priceNotConfigured'));
    } catch {
      showErrorToast(t('products.priceLoadError'));
    }

    appendItem(
      {
        productId: product.id,
        productName: product.name,
        productCode: product.fullCode,
        warehouseId: defaultWarehouse?.id || '',
        quantity: 1,
        unitPrice,
        discountPercent: 0,
        taxRate,
        notes: '',
      },
      { shouldFocus: false }
    );
  };

  const handleAddCustomLine = () => {
    appendItem(
      {
        description: '',
        quantity: 1,
        unitPrice: 0,
        discountPercent: 0,
        taxRate: 0,
        notes: '',
      },
      { focusName: `items.${items.length}.description` }
    );
  };

  const handleCustomerSelect = (customerId: string) => {
    setValue('customerId', customerId);
    setValue('supplierId', undefined);
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      setValue('contactName', customer.contactName || '');
      setValue('email', customer.email || '');
      setValue('phone', customer.phone || '');
      setValue('companyName', customer.companyName || '');
      // Auto-fill shipping address if checkbox is checked
      if (useCustomerAddress) {
        setValue('shippingAddress', customer.addressLine1 || '');
        setValue('shippingCity', customer.city || '');
        setValue('shippingCountry', customer.country || '');
      }
    }
  };

  // CounterpartyPicker delivers a unified callback for both kinds; this
  // routes to the correct form fields and clears the other side.
  const handleCounterpartySelect = (sel: import('@/components/trading/sales/counterparty-picker').CounterpartySelection) => {
    if (sel.kind === 'customer') {
      handleCustomerSelect(sel.id);
      return;
    }
    setValue('supplierId', sel.id);
    setValue('customerId', undefined);
    setValue('contactName', sel.contactName || '');
    setValue('email', sel.email || '');
    setValue('phone', sel.phone || '');
    setValue('companyName', sel.companyName || '');
  };

  const handleCounterpartyClear = () => {
    setValue('customerId', undefined);
    setValue('supplierId', undefined);
  };

  const handleCustomerCreated = (customer: { id: string; companyName: string; contactName: string; email: string; phone: string | null }) => {
    setValue('customerId', customer.id);
    setValue('companyName', customer.companyName || '');
    setValue('contactName', customer.contactName || '');
    setValue('email', customer.email || '');
    setValue('phone', customer.phone || '');
  };

  const handleUseCustomerAddressChange = (checked: boolean) => {
    setUseCustomerAddress(checked);
    if (checked && selectedCustomerId) {
      const customer = customers.find((c) => c.id === selectedCustomerId);
      if (customer) {
        setValue('shippingAddress', customer.addressLine1 || '');
        setValue('shippingCity', customer.city || '');
        setValue('shippingCountry', customer.country || '');
      }
    } else if (!checked) {
      setValue('shippingAddress', '');
      setValue('shippingCity', '');
      setValue('shippingCountry', '');
    }
  };

  const onSubmit = (data: SalesOrderForm) => {
    if (!data.contactName || !data.email) {
      showErrorToast(t('sales.validation.enterContactAndEmail'));
      return;
    }

    if (data.items.length === 0) {
      showErrorToast(t('sales.validation.addProduct'));
      return;
    }

    // Validate all PRODUCT items have a warehouse; service lines don't need one
    const missingWarehouse = data.items.some((item) => item.productId && !item.warehouseId);
    if (missingWarehouse) {
      showErrorToast(t('sales.validation.selectWarehouse'));
      return;
    }

    // Validate every line has either a product or a typed description
    const missingDescription = data.items.some((item) => !item.productId && !item.description?.trim());
    if (missingDescription) {
      showErrorToast(t('sales.validation.enterDescription'));
      return;
    }

    createMutation.mutate({
      quoteId: data.quoteId || undefined,
      tradingCustomerId: data.customerId || undefined,
      tradingSupplierId: data.supplierId || undefined,
      companyName: data.companyName || undefined,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone || undefined,
      shippingAddress: data.shippingAddress || undefined,
      shippingCity: data.shippingCity || undefined,
      shippingCountry: data.shippingCountry || undefined,
      requiredDate: data.requiredDate ? new Date(data.requiredDate) : undefined,
      currency: data.currency,
      paymentTerms: data.paymentTerms || undefined,
      notes: data.notes || undefined,
      items: data.items.map((item) => ({
        productId: item.productId || undefined,
        warehouseId: item.warehouseId || undefined,
        description: item.productId ? undefined : item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discountPercent: item.discountPercent,
        taxRate: item.taxRate,
        notes: item.notes || undefined,
      })),
    });
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/trading/sales/orders"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Link>
          <h1 className="text-2xl font-bold">{t('sales.orders.addOrder')}</h1>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t('common.save')}
        </Button>
      </div>

      <form className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Customer Section */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <User className="h-5 w-5" />
              {t('sales.orders.customer')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('sales.orders.selectCustomer')}</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <CounterpartyPicker
                      customerId={watchedValues.customerId}
                      supplierId={watchedValues.supplierId}
                      onSelect={handleCounterpartySelect}
                      onClear={handleCounterpartyClear}
                    />
                  </div>
                  <CreateCustomerModal onCustomerCreated={handleCustomerCreated} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">{t('sales.orders.company')}</Label>
                <Input
                  id="companyName"
                  {...register('companyName')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName">{t('sales.orders.contactName')} *</Label>
                <Input
                  id="contactName"
                  {...register('contactName', { required: true })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('sales.orders.email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { required: true })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('sales.orders.phone')}</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                />
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 font-semibold">
                <MapPin className="h-5 w-5" />
                {t('sales.orders.shippingAddress')}
              </h2>
              {selectedCustomer && (selectedCustomer.addressLine1 || selectedCustomer.city || selectedCustomer.country) && (
                <div className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={useCustomerAddress}
                    onCheckedChange={handleUseCustomerAddressChange}
                  />
                  <span
                    onClick={() => handleUseCustomerAddressChange(!useCustomerAddress)}
                    className="text-muted-foreground cursor-pointer select-none"
                  >
                    {t('sales.orders.useCustomerAddress')}
                  </span>
                </div>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2 sm:col-span-3">
                <Label htmlFor="shippingAddress">{t('sales.orders.address')}</Label>
                <Input
                  id="shippingAddress"
                  {...register('shippingAddress')}
                  placeholder={t('sales.orders.addressPlaceholder')}
                  disabled={useCustomerAddress && !!selectedCustomer?.addressLine1}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shippingCity">{t('sales.orders.city')}</Label>
                <Input
                  id="shippingCity"
                  {...register('shippingCity')}
                  disabled={useCustomerAddress && !!selectedCustomer?.city}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shippingCountry">{t('sales.orders.country')}</Label>
                <Input
                  id="shippingCountry"
                  {...register('shippingCountry')}
                  disabled={useCustomerAddress && !!selectedCustomer?.country}
                />
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calendar className="h-5 w-5" />
              {t('sales.orders.orderDetails')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="requiredDate">{t('sales.orders.requiredDate')}</Label>
                <Input
                  type="date"
                  id="requiredDate"
                  {...register('requiredDate')}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('sales.orders.currency')}</Label>
                <Combobox
                  options={[
                    { value: 'EUR', label: 'EUR' },
                    { value: 'USD', label: 'USD' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'TRY', label: 'TRY' },
                  ]}
                  value={watchedValues.currency}
                  onValueChange={(value) => setValue('currency', value)}
                  placeholder={t('sales.orders.currency')}
                  searchPlaceholder="Search..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentTerms">{t('sales.orders.paymentTerms')}</Label>
                <Input
                  id="paymentTerms"
                  {...register('paymentTerms')}
                  placeholder={t('sales.orders.paymentTermsPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Order Items */}
          <section aria-labelledby="order-items-heading" className="border-border bg-card border">
            <div className="space-y-4 p-4 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2
                    id="order-items-heading"
                    className="flex items-center gap-2 font-sans text-base font-semibold"
                  >
                    <Package className="text-muted-foreground h-4 w-4" />
                    {t('sales.orders.orderItems')}
                    {items.length > 0 && (
                      <span className="bg-muted px-2 py-0.5 text-xs tabular-nums">
                        {items.length}
                      </span>
                    )}
                  </h2>
                  <p className="text-muted-foreground mt-1.5 text-sm">
                    {t('sales.orders.itemsHelp')}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 gap-2"
                  onClick={handleAddCustomLine}
                >
                  <FileText className="text-muted-foreground h-4 w-4" />
                  {t('sales.orders.addServiceLine')}
                </Button>
              </div>
              <OrderProductPicker items={watchedValues.items ?? []} onAdd={handleAddProduct} />
            </div>

            {/* Items List */}
            {items.length > 0 && (
              <div className="divide-y border-t">
                {items.map((field, index) => {
                  const item = watchedValues.items[index];
                  const discountedPrice =
                    (item?.unitPrice || 0) * (1 - (item?.discountPercent || 0) / 100);
                  const lineSubtotal = (item?.quantity || 0) * discountedPrice;
                  const lineTax = lineSubtotal * ((item?.taxRate || 0) / 100);
                  const lineTotal = lineSubtotal + lineTax;

                  return (
                    <div key={field.id} className="space-y-4 p-4 sm:p-5">
                      <div className="flex items-start gap-3">
                        <span className="bg-muted flex h-7 w-7 shrink-0 items-center justify-center text-xs font-medium tabular-nums">
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          {field.productId ? (
                            <>
                              <p className="text-sm font-medium">{field.productName}</p>
                              <p className="text-muted-foreground font-mono text-xs">
                                {field.productCode}
                              </p>
                            </>
                          ) : (
                            <div className="space-y-1">
                              <Label htmlFor={`item-${field.id}-description`} className="text-xs">
                                {t('sales.orders.customLineDescription')}
                              </Label>
                              <Input
                                id={`item-${field.id}-description`}
                                {...register(`items.${index}.description`)}
                                placeholder={t('sales.orders.customLineDescriptionPlaceholder')}
                              />
                            </div>
                          )}
                        </div>
                        <div className="hidden text-right sm:block">
                          <p className="text-muted-foreground text-xs">
                            {t('sales.orders.lineTotal')}
                          </p>
                          <p className="mt-0.5 text-sm font-semibold tabular-nums">
                            {formatCurrency(lineTotal, watchedValues.currency)}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive shrink-0"
                          aria-label={t('sales.orders.removeItem', {
                            name:
                              field.productName ||
                              item?.description ||
                              t('sales.orders.addServiceLine'),
                          })}
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
                        {field.productId && (
                          <div className="col-span-2 space-y-1">
                            <Label className="text-xs">{t('sales.orders.warehouse')}</Label>
                            <Combobox
                              options={warehouses.map((w) => ({
                                value: w.id,
                                label: `${w.code} - ${w.name}`,
                              }))}
                              value={item?.warehouseId || ''}
                              onValueChange={(value) =>
                                setValue(`items.${index}.warehouseId`, value)
                              }
                              placeholder={t('sales.orders.selectWarehouse')}
                              searchPlaceholder="Search..."
                            />
                          </div>
                        )}
                        <div className="space-y-1">
                          <Label htmlFor={`item-${field.id}-quantity`} className="text-xs">
                            {t('sales.orders.quantity')}
                          </Label>
                          <Input
                            id={`item-${field.id}-quantity`}
                            type="number"
                            {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                            min="1"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`item-${field.id}-price`} className="text-xs">
                            {t('sales.orders.unitPrice')} ({watchedValues.currency})
                          </Label>
                          <Input
                            id={`item-${field.id}-price`}
                            type="number"
                            {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`item-${field.id}-discount`} className="text-xs">
                            {t('sales.orders.discount')} %
                          </Label>
                          <Input
                            id={`item-${field.id}-discount`}
                            type="number"
                            {...register(`items.${index}.discountPercent`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            max="100"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`item-${field.id}-tax`} className="text-xs">
                            {t('sales.orders.tax')} %
                          </Label>
                          <Input
                            id={`item-${field.id}-tax`}
                            type="number"
                            {...register(`items.${index}.taxRate`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            max="100"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end border-t pt-3 sm:hidden">
                        <span className="text-sm">
                          <span className="text-muted-foreground">
                            {t('sales.orders.lineTotal_label')}
                          </span>
                          <span className="font-medium">
                            {formatCurrency(lineTotal, watchedValues.currency)}
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Notes */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('sales.orders.notes')}
            </h2>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('sales.orders.internalNotes')}</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder={t('sales.orders.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Sidebar - Order Summary */}
        <div className="space-y-6">
          <div className="border border-border bg-card p-6 sticky top-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calculator className="h-5 w-5" />
              {t('sales.orders.summary')}
            </h2>

            <div className="space-y-3 text-sm">
              {selectedCustomer && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.orders.customer')}</span>
                  <span className="font-medium">{selectedCustomer.companyName || selectedCustomer.contactName}</span>
                </div>
              )}

              {watchedValues.requiredDate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.orders.requiredDate')}</span>
                  <span className="font-medium">
                    {format(new Date(watchedValues.requiredDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.orders.items')}</span>
                <span className="font-medium">{items.length}</span>
              </div>

              <div className="border-t my-3" />

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.orders.subtotal')}</span>
                <span>{formatCurrency(subtotal, watchedValues.currency)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.orders.tax')}</span>
                <span>{formatCurrency(taxAmount, watchedValues.currency)}</span>
              </div>

              <div className="flex justify-between text-base pt-2 border-t">
                <span className="font-semibold">{t('sales.orders.total')}</span>
                <span className="font-bold">
                  {formatCurrency(total, watchedValues.currency)}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-2">
              <Button
                type="button"
                className="w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={createMutation.isPending || items.length === 0}
              >
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('sales.orders.createDraft')}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                {t('sales.orders.draftNote')}
              </p>
            </div>

            {/* Validation Warnings */}
            {(!watchedValues.contactName || !watchedValues.email || items.length === 0) && (
              <div className="mt-4 space-y-2">
                {!watchedValues.contactName && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.enterContactName')}</p>
                )}
                {!watchedValues.email && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.enterEmail')}</p>
                )}
                {items.length === 0 && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.addAtLeastOneProduct')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
